import React, { useEffect, useState } from "react";
import { withKey, stripKey } from "../lib/uid";
import { api, formatApiError } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { DatePickerUS } from "../components/ui/date-picker-us";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Plus,
  Pencil,
  Trash2,
  PackageSearch,
  Zap,
  X,
} from "lucide-react";
import { brl } from "../lib/format";
import { toast } from "sonner";
import { useConfirm } from "../hooks/useConfirm";
import { FormField } from "../components/ui/form-field";

const emptyTpl = { name: "", description: "", items: [] };
const emptyItem = {
  name: "",
  category: "produto",
  quantity: 1,
  unit_price: 0,
  supplier: "",
};

export default function ExpenseTemplates() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [applying, setApplying] = useState(null);

  async function load() {
    const { data } = await api.get("/expense-templates");
    setItems(data);
  }
  useEffect(() => {
    load();
  }, []);

  async function remove(t) {
    if (!(await confirm({
      title: `Excluir template "${t.name}"?`,
      destructive: true,
      confirmText: "Excluir",
    }))) return;
    try {
      await api.delete(`/expense-templates/${t.id}`);
      toast.success("Template excluído");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function apply(t) {
    setApplying(t);
  }

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            Compras recorrentes
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <PackageSearch size={26} /> Kits de compra
          </h1>
          <div className="text-sm text-slate-500 mt-1">
            Cadastre kits prontos (ex: kit cliente novo, kit faxina mensal) e
            aplique com um clique
          </div>
        </div>
        <Button
          data-testid="template-new-btn"
          onClick={() => {
            setEditing({ ...emptyTpl, items: [] });
            setOpen(true);
          }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus size={16} className="mr-1" /> Novo kit
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((t) => {
          const total = (t.items || []).reduce(
            (s, i) =>
              s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0),
            0,
          );
          return (
            <div
              key={t.id}
              data-testid={`template-card-${t.id}`}
              className="cm-card p-5 flex flex-col"
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">
                    {t.name}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {t.description || "—"}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => {
                      setEditing(t);
                      setOpen(true);
                    }}
                    data-testid={`template-edit-${t.id}`}
                    className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                    title="Editar"
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    onClick={() => remove(t)}
                    data-testid={`template-delete-${t.id}`}
                    className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                    title="Excluir"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <div className="mt-3 flex-1">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">
                  {t.items?.length || 0} item(ns)
                </div>
                <div className="flex flex-wrap gap-1">
                  {(t.items || []).slice(0, 6).map((it, i) => (
                    <span
                      key={`${i}-${it.name}`}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200"
                    >
                      {it.name} ×{it.quantity}
                    </span>
                  ))}
                  {(t.items || []).length > 6 && (
                    <span className="text-[11px] text-slate-500">
                      +{(t.items || []).length - 6} mais
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                    Total estimado
                  </div>
                  <div className="font-bold text-lg">{brl(total)}</div>
                </div>
                <Button
                  data-testid={`template-apply-${t.id}`}
                  onClick={() => apply(t)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Zap size={14} className="mr-1" /> Aplicar
                </Button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="cm-card p-10 text-center text-slate-500 col-span-full">
            Nenhum kit cadastrado.
          </div>
        )}
      </div>

      <TemplateDialog
        open={open}
        onOpenChange={setOpen}
        item={editing}
        onSaved={() => {
          setOpen(false);
          load();
        }}
      />
      <ApplyDialog
        template={applying}
        onClose={() => setApplying(null)}
      />
    </AdminLayout>
  );
}

function ApplyDialog({ template, onClose }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [supplier, setSupplier] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (template) {
      setDate(new Date().toISOString().slice(0, 10));
      setSupplier("");
    }
  }, [template]);
  if (!template) return null;
  const total = (template.items || []).reduce(
    (s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0),
    0,
  );
  async function go() {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/expense-templates/${template.id}/apply`,
        { date, supplier: supplier || undefined },
      );
      toast.success(
        `${data.applied} compras criadas — total ${brl(data.total)}`,
      );
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={!!template} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="apply-template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap size={18} /> Aplicar kit — {template.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
            Serão criadas <strong>{template.items?.length || 0} compras</strong>{" "}
            com total estimado de <strong>{brl(total)}</strong>.
          </div>
          <FormField label="Data da Compra">
            <DatePickerUS
              testId="apply-template-date"
              value={date}
              onChange={setDate}
            />
            <span className="text-xs text-slate-500 mt-1 block">
              Use uma data passada para registrar uma compra retroativa.
            </span>
</FormField>
          <FormField label="Fornecedor (opcional)">
            
            <input
              className="cm-input"
              placeholder="Se vazio, usa o fornecedor de cada item"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
            />
</FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            data-testid="apply-template-confirm"
            onClick={go}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Zap size={14} className="mr-1" />
            {busy ? "Aplicando..." : "Aplicar agora"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplateDialog({ open, onOpenChange, item, onSaved }) {
  const keyed = (t) => ({ ...t, items: (t.items || []).map(withKey) });
  const [form, setForm] = useState(keyed(item || emptyTpl));
  useEffect(() => {
    setForm(keyed(item || emptyTpl));
  }, [item, open]);
  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  function setItem(idx, k, v) {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)),
    }));
  }
  function addItem() {
    setForm((f) => ({ ...f, items: [...(f.items || []), withKey(emptyItem)] }));
  }
  function removeItem(idx) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, i) => i !== idx),
    }));
  }
  async function save() {
    try {
      const body = {
        ...form,
        items: (form.items || []).map((it) => ({
          ...stripKey(it),
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
        })),
      };
      if (item?.id) await api.patch(`/expense-templates/${item.id}`, body);
      else await api.post("/expense-templates", body);
      toast.success("Kit salvo");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }
  const total = (form.items || []).reduce(
    (s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0),
    0,
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="template-dialog"
      >
        <DialogHeader>
          <DialogTitle>{item?.id ? "Editar kit" : "Novo kit"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FormField label="Nome do Kit">
              
              <input
                data-testid="template-field-name"
                className="cm-input"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Ex: Kit cliente novo"
              />
</FormField>
            <FormField label="Descrição">
              
              <input
                className="cm-input"
                value={form.description || ""}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Quando usar este kit"
              />
</FormField>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-slate-700">Itens</div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addItem}
                data-testid="template-add-item"
              >
                <Plus size={14} className="mr-1" /> Adicionar item
              </Button>
            </div>
            <div className="space-y-2">
              {(form.items || []).length === 0 && (
                <div className="text-xs text-slate-500 p-3 text-center border border-dashed rounded-lg">
                  Nenhum item. Adicione produtos a este kit.
                </div>
              )}
              {(form.items || []).map((it, idx) => (
                <div
                  key={it._k}
                  className="grid grid-cols-12 gap-2 items-end p-2 border border-slate-200 rounded-lg"
                  data-testid={`template-item-${idx}`}
                >
                  <input
                    className="cm-input col-span-12 md:col-span-4"
                    placeholder="Produto"
                    value={it.name}
                    onChange={(e) => setItem(idx, "name", e.target.value)}
                  />
                  <select
                    className="cm-input col-span-6 md:col-span-2"
                    value={it.category}
                    onChange={(e) => setItem(idx, "category", e.target.value)}
                  >
                    <option value="produto">Produto</option>
                    <option value="equipamento">Equipamento</option>
                    <option value="uniforme">Uniforme</option>
                    <option value="outro">Outro</option>
                  </select>
                  <input
                    className="cm-input col-span-6 md:col-span-2"
                    placeholder="Qtd"
                    type="number"
                    value={it.quantity}
                    onChange={(e) => setItem(idx, "quantity", e.target.value)}
                  />
                  <input
                    className="cm-input col-span-6 md:col-span-2"
                    placeholder="Preço unit."
                    type="number"
                    step="0.01"
                    value={it.unit_price}
                    onChange={(e) =>
                      setItem(idx, "unit_price", e.target.value)
                    }
                  />
                  <input
                    className="cm-input col-span-5 md:col-span-1"
                    placeholder="Fornecedor"
                    value={it.supplier || ""}
                    onChange={(e) => setItem(idx, "supplier", e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    className="col-span-1 p-2 text-red-600 hover:bg-red-50 rounded"
                    data-testid={`template-remove-item-${idx}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <span className="text-sm text-slate-600">
              Total estimado do kit
            </span>
            <span className="text-2xl font-bold">{brl(total)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            data-testid="template-save-btn"
            onClick={save}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
